import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allCategories, getPopularArticles, searchArticles } from "@/utils/data";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  usePageMeta(
    query ? `搜索“${query}” - 自慰健康生活资讯` : "站内搜索 - 自慰健康生活资讯",
    "在自慰健康生活资讯站内搜索自慰、心理健康、身体照护与自我关怀相关的科普内容。",
    "自慰,搜索,健康生活,心理健康,自我关怀",
  );

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = query.trim() ? searchArticles(query) : [];
  const popular = getPopularArticles(6);
  const hotTags = ["科学认知", "羞耻感", "睡眠", "润滑", "正念", "边界", "放松", "育儿沟通"];

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: "站内搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">搜索健康资讯</h1>
          <p className="mt-2 text-sm text-foreground-600">输入关键字，例如“睡眠”“羞耻感”“护理”，查找你关心的内容。</p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="text"
                name="keyword"
                placeholder="搜索文章标题、摘要或标签…"
                className="w-full h-12 pl-11 pr-4 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">热门搜索：</span>
            {hotTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSearchParams({ q: tag })}
                className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900 transition-colors cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-10">
        {query.trim() ? (
          <>
            <p className="text-sm text-foreground-600 mb-6">
              关键词 “<span className="text-foreground-900 font-medium">{query}</span>” 共找到{" "}
              <span className="text-primary-700 font-medium">{results.length}</span> 篇资讯
            </p>
            {results.length === 0 ? (
              <div className="py-16 text-center rounded-lg bg-background-100 border border-background-200">
                <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
                <p className="mt-5 text-foreground-700">暂无相关资讯</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或从下方分类中浏览。</p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {allCategories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="px-4 py-2 rounded-md bg-primary-50 text-primary-700 text-sm hover:bg-primary-100 transition-colors"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {results.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">按分类浏览</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="group flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors"
                  >
                    <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 text-xl group-hover:bg-primary-500 group-hover:text-background-50 transition-colors shrink-0">
                      <i className={category.icon}></i>
                    </span>
                    <div>
                      <h3 className="text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                        {category.name}
                      </h3>
                      <p className="mt-1 text-sm text-foreground-600 line-clamp-2">{category.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="lg:col-span-1">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">大家都在看</h2>
              <div className="flex flex-col gap-3">
                {popular.map((article, index) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" rank={index + 1} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}